import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { createBase, updateBase, getBaseById } from '../services/api';
import { Section, PageHeading, Heading, Text } from '../components/ui';

const CreateEditKernel = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // If present, we're editing
    const { user } = useAuth();
    const isEditing = !!id;

    const [title, setTitle] = useState('');
    const [slotsJson, setSlotsJson] = useState('{\n  "protein": "chicken, beef, tofu",\n  "vegetable": "broccoli, carrots, spinach",\n  "grain": "rice, quinoa, pasta"\n}');
    const [jsonError, setJsonError] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [imageUrls, setImageUrls] = useState(['']);
    
    // Context fields
    const [context, setContext] = useState('');
    const [techniqueTips, setTechniqueTips] = useState('');
    const [commonMistakes, setCommonMistakes] = useState('');
    const [equipment, setEquipment] = useState('');
    const [difficultyLevel, setDifficultyLevel] = useState('intermediate');
    const [timeRange, setTimeRange] = useState('');
    const [temperatureSettings, setTemperatureSettings] = useState('');

    // Load kernel data if editing
    useEffect(() => {
        if (isEditing && id) {
            fetchKernel();
        }
    }, [isEditing, id]);

    const fetchKernel = async () => {
        try {
            setLoading(true);
            const token = await user.getIdToken();
            const kernel = await getBaseById(id, token, true); // includeImages = true
            setTitle(kernel.title);
            setSlotsJson(JSON.stringify(kernel.slots || {}, null, 2));
            setImageUrls(kernel.images && kernel.images.length > 0 ? kernel.images : ['']);
            
            // Load context fields
            setContext(kernel.context || '');
            setTechniqueTips(kernel.technique_tips || '');
            setCommonMistakes(kernel.common_mistakes || '');
            setEquipment(kernel.equipment || '');
            setDifficultyLevel(kernel.difficulty_level || 'intermediate');
            setTimeRange(kernel.time_range || '');
            setTemperatureSettings(kernel.temperature_settings || '');
        } catch (err) {
            console.error('Error fetching kernel:', err);
            setError('Failed to load kernel');
        } finally {
            setLoading(false);
        }
    };

    const validateJson = (jsonString) => {
        try {
            JSON.parse(jsonString);
            setJsonError('');
            return true;
        } catch (err) {
            setJsonError(`Invalid JSON: ${err.message}`);
            return false;
        }
    };

    const handleSlotsChange = (value) => {
        setSlotsJson(value);
        validateJson(value);
    };

    const addQuickSlot = (slotName, slotValue) => {
        try {
            const currentSlots = JSON.parse(slotsJson);
            currentSlots[slotName] = slotValue;
            const newJson = JSON.stringify(currentSlots, null, 2);
            setSlotsJson(newJson);
            setJsonError('');
        } catch (err) {
            // If current JSON is invalid, create new object
            const newSlots = { [slotName]: slotValue };
            setSlotsJson(JSON.stringify(newSlots, null, 2));
            setJsonError('');
        }
    };

    const addImageUrl = () => {
        setImageUrls([...imageUrls, '']);
    };

    const removeImageUrl = (index) => {
        if (imageUrls.length > 1) {
            setImageUrls(imageUrls.filter((_, i) => i !== index));
        }
    };

    const updateImageUrl = (index, value) => {
        const newUrls = [...imageUrls];
        newUrls[index] = value;
        setImageUrls(newUrls);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!title.trim()) {
            setError('Title is required');
            return;
        }

        if (!validateJson(slotsJson)) {
            setError('Please fix JSON syntax errors');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            const token = await user.getIdToken();
            
            // Filter out empty image URLs
            const validImageUrls = imageUrls.filter(url => url.trim() !== '');
            
            const kernelData = {
                title: title.trim(),
                slots: JSON.parse(slotsJson),
                imageUrls: validImageUrls,
                context: context.trim(),
                technique_tips: techniqueTips.trim(),
                common_mistakes: commonMistakes.trim(),
                equipment: equipment.trim(),
                difficulty_level: difficultyLevel,
                time_range: timeRange.trim(),
                temperature_settings: temperatureSettings.trim()
            };

            if (isEditing) {
                await updateBase(id, kernelData, token);
            } else {
                await createBase(kernelData, token);
            }

            navigate('/bases');
        } catch (err) {
            console.error('Error saving base:', err);
            setError(`Failed to ${isEditing ? 'update' : 'create'} base`);
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <Section>
                <PageHeading>{isEditing ? 'Edit Base' : 'Create Base'}</PageHeading>
                <Text>Please log in to manage bases.</Text>
            </Section>
        );
    }

    if (loading && isEditing) {
        return (
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-8 lg:px-8 xl:px-12 py-16">
                    <div className="bg-neutral-0 hairline minimal-padding">
                        <Text>Loading base...</Text>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-8 lg:px-8 xl:px-12 py-16">
                {/* Header section - no container */}
                <div className="vspace-xl mb-8">
                    <PageHeading size="hero">
                        {isEditing ? 'EDIT BASE' : 'NEW BASE'}
                    </PageHeading>
                    <Text size="lg" className="font-mono uppercase tracking-wider text-neutral-900">
                        CREATE RECIPE TEMPLATES • DEFINE INGREDIENT SLOTS
                    </Text>
                </div>

                {/* Single white container for all content */}
                <div className="bg-neutral-0 hairline minimal-padding">
                    <form onSubmit={handleSubmit} className="vspace-xl">
                        {error && (
                            <div className="hairline p-4 bg-neutral-50 text-neutral-900">
                                <Text size="sm" className="font-mono uppercase tracking-wider">
                                    ERROR: {error}
                                </Text>
                            </div>
                        )}

                        <div>
                            <Heading level={3}>Template Details</Heading>
                            
                            <div>
                                <label className="form-label">
                                    BASE TITLE
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="form-input"
                                    placeholder="e.g., Basic Stir Fry Template"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <Heading level={3}>Template Slots (JSON)</Heading>
                            
                            <Text size="sm" className="text-neutral-900 opacity-60 mb-4">
                                Define ingredient categories and example options. Each key represents a slot type.
                            </Text>

                            {/* Quick Add Buttons */}
                            <div className="flex flex-wrap gap-2 mb-4">
                                <button
                                    type="button"
                                    onClick={() => addQuickSlot('protein', 'chicken, beef, tofu, fish')}
                                    className="px-3 py-2 bg-neutral-50 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-0 transition-colors"
                                >
                                    + PROTEIN
                                </button>
                                <button
                                    type="button"
                                    onClick={() => addQuickSlot('vegetable', 'broccoli, carrots, bell peppers')}
                                    className="px-3 py-2 bg-neutral-50 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-0 transition-colors"
                                >
                                    + VEGETABLE
                                </button>
                                <button
                                    type="button"
                                    onClick={() => addQuickSlot('grain', 'rice, quinoa, pasta, bread')}
                                    className="px-3 py-2 bg-neutral-50 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-0 transition-colors"
                                >
                                    + GRAIN
                                </button>
                                <button
                                    type="button"
                                    onClick={() => addQuickSlot('sauce', 'soy sauce, olive oil, butter')}
                                    className="px-3 py-2 bg-neutral-50 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-0 transition-colors"
                                >
                                    + SAUCE
                                </button>
                            </div>

                            <textarea
                                value={slotsJson}
                                onChange={(e) => handleSlotsChange(e.target.value)}
                                rows={12}
                                className={`form-input font-mono text-sm ${jsonError ? 'border-red-500' : ''}`}
                                placeholder="Enter JSON configuration..."
                            />

                            {jsonError && (
                                <div className="mt-2 p-3 bg-red-50 hairline">
                                    <Text size="sm" className="text-red-700 font-mono">
                                        {jsonError}
                                    </Text>
                                </div>
                            )}
                        </div>

                        <div>
                            <Heading level={3}>Technique Context & Instructions</Heading>
                            
                            <Text size="sm" className="text-neutral-900 opacity-60 mb-4">
                                Provide detailed information about this cooking technique to help the AI generate better instructions.
                            </Text>

                            {/* Basic Info Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div>
                                    <label className="form-label">
                                        DIFFICULTY LEVEL
                                    </label>
                                    <select
                                        value={difficultyLevel}
                                        onChange={(e) => setDifficultyLevel(e.target.value)}
                                        className="form-input"
                                    >
                                        <option value="beginner">Beginner</option>
                                        <option value="intermediate">Intermediate</option>
                                        <option value="advanced">Advanced</option>
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="form-label">
                                        TIME RANGE
                                    </label>
                                    <input
                                        type="text"
                                        value={timeRange}
                                        onChange={(e) => setTimeRange(e.target.value)}
                                        className="form-input"
                                        placeholder="e.g., 5-15 minutes, 30-60 minutes"
                                    />
                                </div>
                                
                                <div>
                                    <label className="form-label">
                                        TEMPERATURE SETTINGS
                                    </label>
                                    <input
                                        type="text"
                                        value={temperatureSettings}
                                        onChange={(e) => setTemperatureSettings(e.target.value)}
                                        className="form-input"
                                        placeholder="e.g., medium-high heat, 350°F"
                                    />
                                </div>
                            </div>

                            {/* Context Section */}
                            <div className="mb-6">
                                <label className="form-label">
                                    TECHNIQUE CONTEXT
                                </label>
                                <textarea
                                    value={context}
                                    onChange={(e) => setContext(e.target.value)}
                                    className="form-input"
                                    rows={4}
                                    placeholder="Describe the cooking technique, its purpose, and when to use it. This helps the AI understand the context and provide better instructions."
                                />
                                <div className="text-xs text-neutral-900 opacity-60 mt-1">
                                    Explain what this technique is, why it's used, and the science behind it.
                                </div>
                            </div>

                            {/* Technique Tips */}
                            <div className="mb-6">
                                <label className="form-label">
                                    TECHNIQUE TIPS & BEST PRACTICES
                                </label>
                                <textarea
                                    value={techniqueTips}
                                    onChange={(e) => setTechniqueTips(e.target.value)}
                                    className="form-input"
                                    rows={4}
                                    placeholder="Share expert tips, tricks, and best practices for mastering this technique."
                                />
                                <div className="text-xs text-neutral-900 opacity-60 mt-1">
                                    Include timing tips, visual cues, and professional secrets.
                                </div>
                            </div>

                            {/* Common Mistakes */}
                            <div className="mb-6">
                                <label className="form-label">
                                    COMMON MISTAKES TO AVOID
                                </label>
                                <textarea
                                    value={commonMistakes}
                                    onChange={(e) => setCommonMistakes(e.target.value)}
                                    className="form-input"
                                    rows={4}
                                    placeholder="List common mistakes people make with this technique and how to avoid them."
                                />
                                <div className="text-xs text-neutral-900 opacity-60 mt-1">
                                    Help users avoid pitfalls and achieve better results.
                                </div>
                            </div>

                            {/* Equipment */}
                            <div className="mb-6">
                                <label className="form-label">
                                    REQUIRED EQUIPMENT
                                </label>
                                <textarea
                                    value={equipment}
                                    onChange={(e) => setEquipment(e.target.value)}
                                    className="form-input"
                                    rows={3}
                                    placeholder="List the essential tools and equipment needed for this technique."
                                />
                                <div className="text-xs text-neutral-900 opacity-60 mt-1">
                                    Include specific pan types, utensils, thermometers, etc.
                                </div>
                            </div>
                        </div>

                        <div>
                            <Heading level={3}>Template Images</Heading>
                            
                            <Text size="sm" className="text-neutral-900 opacity-60 mb-4">
                                Add image URLs to showcase this cooking technique. These will appear in pop-ups and live sessions.
                            </Text>

                            <div className="space-y-3">
                                {imageUrls.map((url, index) => (
                                    <div key={index} className="flex gap-2 items-center">
                                        <input
                                            type="url"
                                            value={url}
                                            onChange={(e) => updateImageUrl(index, e.target.value)}
                                            className="form-input flex-1"
                                            placeholder="https://example.com/image.jpg"
                                        />
                                        {imageUrls.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeImageUrl(index)}
                                                className="px-3 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                ))}
                                
                                <button
                                    type="button"
                                    onClick={addImageUrl}
                                    className="px-3 py-2 bg-neutral-50 hairline font-mono text-xs uppercase tracking-wider hover:bg-neutral-0 transition-colors"
                                >
                                    + ADD IMAGE URL
                                </button>

                                <div className="mt-3 p-3 bg-neutral-50 hairline">
                                    <Text size="sm" className="text-neutral-900 opacity-60">
                                        💡 <strong>Quick placeholders:</strong> Try unsplash.com URLs like:
                                        <br />• https://images.unsplash.com/photo-1556909114-f6e7ad7d3136
                                        <br />• https://images.unsplash.com/photo-1551782450-a2132b4ba21d
                                    </Text>
                                </div>
                            </div>
                        </div>

                        <div className="flex space-x-4 pt-8 hairline-t">
                            <button
                                type="submit"
                                disabled={loading || !!jsonError}
                                className="px-6 py-3 bg-neutral-900 text-neutral-0 hairline font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-40"
                            >
                                {loading ? 'SAVING...' : (isEditing ? 'UPDATE BASE' : 'CREATE BASE')}
                            </button>
                            
                            <button
                                type="button"
                                onClick={() => navigate('/bases')}
                                className="px-6 py-3 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-50 transition-colors"
                            >
                                CANCEL
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateEditKernel; 
